/**
 * Reschedule / cancel dialogs for a lab order, shared by the orders list row
 * menu and the order detail drawer.
 *
 * These used to live inside LabOrderDetailDrawer.jsx, which meant the actions
 * were only reachable after opening a drawer. They are provider-aware — every
 * partner-specific difference (Orange takes a free datetime, Redcliffe takes an
 * integer slot id from its own availability; Orange confirms cancellation by
 * webhook, Redcliffe answers synchronously) comes from utils/labProviders, so
 * both call sites behave identically for both partners.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Stack, Button, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { getProviderMeta } from "../../../utils/labProviders";

const BASE = "https://api.medstown.com";

const T = {
  border:     "#e5e7eb",
  ink:        "#0f172a",
  muted:      "#64748b",
  amber:      "#d97706",
  red:        "#dc2626",
};

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Redcliffe's slot API only answers for today + 4 days. */
const RC_HORIZON_DAYS = 5;

const isoDay = (d) => d.toISOString().slice(0, 10);

/** "2026-07-19" -> "19 Jul, 2026", the label format LabOrder.scheduledDate uses. */
const dateLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}, ${d.getFullYear()}`;
};

/**
 * Statuses past which no partner accepts a change. Exported so the list row
 * menu and the drawer header hide the actions on exactly the same rule — a row
 * that offers "Cancel" on an already-cancelled order is just a guaranteed
 * partner rejection.
 */
export const canModifyOrder = (order) =>
  Boolean(order) && !["Cancelled", "Completed"].includes(order.status);

// ── Reschedule Dialog ───────────────────────────────────────────────────────
//
// Two shapes, because the partners are not alike:
//
//   Orange     — accepts any ISO datetime inside its serviceability window, so
//                a free datetime field is fine.
//   Redcliffe  — books against an INTEGER slot id from its own availability.
//                An arbitrary datetime is not a thing it can accept, so the
//                admin must pick a real slot, and rcSlotId must be sent. The
//                backend rejects a Redcliffe reschedule without one rather than
//                forwarding collection_slot: undefined.
export function RescheduleDialog({ open, onClose, order, onDone }) {
  const [isoDatetime, setIsoDatetime]     = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledSlot, setScheduledSlot] = useState("");
  const [reason, setReason]               = useState("Patient requested reschedule");
  const [saving, setSaving]               = useState(false);

  // Redcliffe-only slot picking
  const [rcDate, setRcDate]         = useState("");
  const [rcSlots, setRcSlots]       = useState([]);
  const [rcSlotId, setRcSlotId]     = useState("");
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);

  const meta = getProviderMeta(order?.provider);
  const isRedcliffe = order?.provider === "redcliffe";

  const lat = order?.location?.latitude;
  const lng = order?.location?.longitude;
  const canPickSlots = isRedcliffe && lat != null && lng != null;

  const rcDates = useMemo(() => {
    const out = [];
    for (let i = 0; i < RC_HORIZON_DAYS; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      out.push(isoDay(d));
    }
    return out;
  }, []);

  useEffect(() => {
    if (open && order) {
      setIsoDatetime(order.slotDatetime || "");
      setScheduledDate(order.scheduledDate || "");
      setScheduledSlot(order.scheduledSlot || "");
      setReason("Patient requested reschedule");
      setRcSlots([]); setRcSlotId(""); setSlotsError(null);
      setRcDate(isRedcliffe ? isoDay(new Date()) : "");
    }
  }, [open, order, isRedcliffe]);

  // Fetch Redcliffe's real availability for the chosen date.
  useEffect(() => {
    if (!open || !canPickSlots || !rcDate) return;
    let cancelled = false;
    setSlotsLoading(true);
    setSlotsError(null);
    setRcSlotId("");
    axios
      .get(`${BASE}/lab/slots`, {
        params: { provider: "redcliffe", date: rcDate, latitude: lat, longitude: lng },
      })
      .then(({ data }) => {
        if (cancelled) return;
        const slots = data?.slots || [];
        setRcSlots(slots);
        if (!slots.length) setSlotsError("Redcliffe has no slots for this date.");
      })
      .catch(() => {
        if (!cancelled) setSlotsError("Couldn't load Redcliffe's slots. Try again.");
      })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [open, canPickSlots, rcDate, lat, lng]);

  async function submit() {
    const body = { rescheduleReason: reason };

    if (isRedcliffe) {
      const slot = rcSlots.find((s) => String(s.slotId) === String(rcSlotId));
      if (!slot) { toast.error("Pick a slot from Redcliffe's availability"); return; }
      body.requestedSlotTime = slot.startTime;
      body.rcSlotId          = slot.slotId;
      body.scheduledDate     = dateLabel(rcDate);
      body.scheduledSlot     = slot.label;
    } else {
      if (!isoDatetime) { toast.error("Pick a new slot datetime"); return; }
      body.requestedSlotTime = isoDatetime;
      body.scheduledDate     = scheduledDate;
      body.scheduledSlot     = scheduledSlot;
    }

    setSaving(true);
    try {
      await axios.post(`${BASE}/admin/lab/orders/${order._id}/reschedule`, body);
      toast.success("Order rescheduled");
      onDone(); onClose();
    } catch (e) {
      // The partner's own refusal (e.g. Redcliffe's "Can't change booking at
      // this stage") arrives in `detail` — surface it, not just our wrapper.
      const d = e.response?.data;
      toast.error(
        d?.detail ? `${d.message || "Reschedule failed"}: ${d.detail}` : (d?.message || "Reschedule failed")
      );
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${T.border}` } }}>
      <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, color: T.ink, pt: 2.2, pb: 0.5 }}>
        Reschedule {meta.label} order
      </DialogTitle>
      <DialogContent sx={{ pt: 1.2, pb: 1 }}>
        <Typography sx={{ fontSize: "0.74rem", color: T.muted, mb: 1.5 }}>
          {meta.reschedule.note}
        </Typography>
        {meta.reschedule.warning && (
          <Box sx={{
            p: 1.2, borderRadius: 1.5, mb: 1.5,
            bgcolor: `${T.amber}0d`, border: `1px solid ${T.amber}33`,
          }}>
            <Typography sx={{ fontSize: "0.72rem", color: T.amber, fontWeight: 600 }}>
              {meta.reschedule.warning}
            </Typography>
          </Box>
        )}
        <Stack spacing={1.2}>
          {isRedcliffe ? (
            <>
              {/* Redcliffe books against a real slot id, so the admin picks from
                  its live availability rather than typing a time. */}
              {!canPickSlots ? (
                <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: `${T.amber}0d`, border: `1px solid ${T.amber}33` }}>
                  <Typography sx={{ fontSize: "0.72rem", color: T.amber, fontWeight: 600 }}>
                    This order has no saved coordinates, so Redcliffe's slots can't be
                    looked up. Reschedule isn't possible from here — ask the customer to
                    reschedule in the app.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TextField
                    select size="small" label="Collection date"
                    value={rcDate}
                    onChange={(e) => setRcDate(e.target.value)}
                    helperText="Redcliffe only publishes slots for the next 5 days."
                  >
                    {rcDates.map((d) => (
                      <MenuItem key={d} value={d}>{dateLabel(d)}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select size="small" label="Slot"
                    value={rcSlotId}
                    onChange={(e) => setRcSlotId(e.target.value)}
                    disabled={slotsLoading || !rcSlots.length}
                    helperText={
                      slotsLoading
                        ? "Loading Redcliffe's availability…"
                        : slotsError || `${rcSlots.length} slot(s) available`
                    }
                    error={Boolean(slotsError)}
                  >
                    {rcSlots.map((s) => (
                      <MenuItem key={s.slotId} value={s.slotId}>
                        {s.label} · {s.available} left
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}
            </>
          ) : (
            <>
              <TextField
                size="small" type="datetime-local"
                label="New slot datetime" InputLabelProps={{ shrink: true }}
                value={isoDatetime ? isoDatetime.slice(0, 16) : ""}
                onChange={(e) => setIsoDatetime(e.target.value)}
                helperText={meta.reschedule.slotHelp}
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small" label='Date label (e.g. "7 Mar, 2024")'
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small" label="Slot label"
                  value={scheduledSlot}
                  onChange={(e) => setScheduledSlot(e.target.value)}
                  fullWidth
                />
              </Stack>
            </>
          )}
          <TextField
            size="small" label="Reason"
            value={reason} onChange={(e) => setReason(e.target.value)}
            multiline rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} sx={{ color: T.muted, textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained" onClick={submit}
          // A Redcliffe reschedule without a real slot id is rejected by the
          // backend, so don't offer the button until one is picked.
          disabled={saving || (isRedcliffe && (!canPickSlots || !rcSlotId))}
          sx={{
            textTransform: "none", fontWeight: 600, borderRadius: 1.5,
            bgcolor: T.ink, "&:hover": { bgcolor: "#1e293b" },
            boxShadow: "none",
          }}>
          {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Confirm reschedule"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Cancel Dialog ───────────────────────────────────────────────────────────
export function CancelDialog({ open, onClose, order, onDone }) {
  const [reason, setReason]           = useState("Admin cancelled");
  const [explanation, setExplanation] = useState("Cancelled by Medstown admin");
  const [saving, setSaving]           = useState(false);

  const meta = getProviderMeta(order?.provider);

  async function submit() {
    setSaving(true);
    try {
      await axios.post(`${BASE}/admin/lab/orders/${order._id}/cancel`, {
        cancellationReason: reason, cancellationExplanation: explanation,
      });
      toast.success("Cancellation initiated");
      onDone(); onClose();
    } catch (e) {
      // The partner's own refusal (e.g. Redcliffe's "Not allowed to cancel this
      // booking as Phlebo has been assigned") arrives in `detail`.
      const d = e.response?.data;
      toast.error(
        d?.detail ? `${d.message || "Cancellation failed"}: ${d.detail}` : (d?.message || "Cancellation failed")
      );
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${T.border}` } }}>
      <DialogTitle sx={{ fontSize: "0.95rem", fontWeight: 700, color: T.ink, pt: 2.2, pb: 0.5 }}>
        Cancel {meta.label} order
      </DialogTitle>
      <DialogContent sx={{ pt: 1.2, pb: 1 }}>
        <Box sx={{
          p: 1.2, borderRadius: 1.5, mb: 1.5,
          bgcolor: `${T.red}0d`, border: `1px solid ${T.red}33`,
        }}>
          {/* Orange confirms via webhook; Redcliffe answers synchronously. Saying
              "confirmation arrives via webhook" for both was wrong for Redcliffe. */}
          <Typography sx={{ fontSize: "0.74rem", color: T.red, fontWeight: 600 }}>
            {meta.cancel.note}
          </Typography>
        </Box>
        {meta.cancel.warning && (
          <Box sx={{
            p: 1.2, borderRadius: 1.5, mb: 1.5,
            bgcolor: `${T.amber}0d`, border: `1px solid ${T.amber}33`,
          }}>
            <Typography sx={{ fontSize: "0.72rem", color: T.amber, fontWeight: 600 }}>
              {meta.cancel.warning}
            </Typography>
          </Box>
        )}
        <Stack spacing={1.2}>
          <TextField size="small" label="Reason" value={reason}
            onChange={(e) => setReason(e.target.value)} />
          <TextField size="small" label="Explanation" value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            multiline rows={3} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={onClose} sx={{ color: T.muted, textTransform: "none" }}>Back</Button>
        <Button
          variant="contained" onClick={submit} disabled={saving}
          sx={{
            textTransform: "none", fontWeight: 600, borderRadius: 1.5,
            bgcolor: T.red, "&:hover": { bgcolor: "#b91c1c" },
            boxShadow: "none",
          }}>
          {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Confirm cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
